// Synergy overview (helix-d3 15:33 PT 2026-10-06): the demo entry point. The board totals, the
// off-track shortfalls with their owners, and one row per synergy, read from the PUBLISHED S1
// commitment-status findings and the commitment-lines revision those findings pin. Every number
// is a bound figure (synergy-loop.v1 `AskHelixNumericBindingDto`): value, measure, pointer, result class,
// server display and a voice derived in code. Underwriting overlays are labelled
// "Adjustment, not a separate commitment" from their promise authority, never from a
// status word, and never enter a board total. Clicking a row runs the loop for that commitment.

import { readSynergyLoopFigure, readSynergyLoopSegment,
  type SynergyLoopSegment } from './synergy-loop.v1';
import type { AskHelixNumericBindingDto } from '../../ask-helix';
import { readSynergyReconciliations, type SynergyReconciliation } from './synergy-reconciliation';
export * from './synergy-reconciliation';

export const SYNERGY_OVERVIEW_SCHEMA_VERSION = 'synergy-overview.v1' as const;
export const SYNERGY_OVERVIEW_PATH = 'v1/views/synergy-overview';
export const SYNERGY_OVERVIEW_FORECAST_TOTAL = 'summary.year_end_forecast';

/** Status keys; `overlay` is decided by promise authority alone. */
export const SYNERGY_OVERVIEW_STATUSES = [
  'on_track',
  'at_risk',
  'off_track',
  'not_yet_measurable',
  'overlay',
] as const;
export type SynergyOverviewStatus = (typeof SYNERGY_OVERVIEW_STATUSES)[number];

export const SYNERGY_OVERVIEW_STATUS_TEXT: Readonly<Record<SynergyOverviewStatus, string>> = {
  on_track: 'On track',
  at_risk: 'At risk',
  off_track: 'Off track',
  not_yet_measurable: 'Not yet measurable',
  overlay: 'Adjustment, not a separate commitment',
};

/** A slot: a computed figure with its as-of, or a typed reason it is not shown. */
export type SynergyOverviewSlot =
  | Readonly<{ state: 'computed'; figure: AskHelixNumericBindingDto; asOf: string;
      dueDate?: string | null }>
  | Readonly<{ state: 'not_available'; reason: string }>;

export type SynergyOverviewShortfall = Readonly<{
  commitmentRef: string;
  label: string;
  owner: string;
  shortfall: AskHelixNumericBindingDto;
  asOf: string;
}>;

export type SynergyOverviewRow = Readonly<{
  commitmentRef: string;
  label: string;
  kind: 'commitment' | 'overlay';
  status: SynergyOverviewStatus;
  statusText: string;
  owner: string;
  target: SynergyOverviewSlot;
  reconciliations?: readonly SynergyReconciliation[];
  planToDate: SynergyOverviewSlot;
  realized: SynergyOverviewSlot;
  /** Plan to date minus realized, as the governed realization row publishes it. */
  behindPlan: SynergyOverviewSlot;
  /** Promise minus the engine's forecast (S1 commitment status). */
  forecastShortfall: SynergyOverviewSlot;
  /** Base fiscal-year landing, from the published landing forecast pinned by status. */
  yearEndForecast?: SynergyOverviewSlot;
  /** Governed difference: committed run-rate minus that same fiscal-year landing. */
  yearEndShortfall?: SynergyOverviewSlot;
}>;

export type SynergyOverviewSource = Readonly<{
  datasetRef: string;
  revisionId: string;
  digest: string;
}>;

/**
 * UAT-24: two or three engine-decided sentences above the tiles. Each figure segment cites a
 * binding in `figures`; each sentence passed the story-relation checker on the server.
 */
export type SynergyOverviewSummary = Readonly<{
  sentences: readonly (readonly SynergyLoopSegment[])[];
  figures: readonly AskHelixNumericBindingDto[];
  /** The one source line for the block. */
  source: string;
}>;

export type SynergyOverviewV1 = Readonly<{
  schemaVersion: typeof SYNERGY_OVERVIEW_SCHEMA_VERSION;
  dealRef: string;
  dealLabel: string;
  asOf: string;
  board: Readonly<{
    target: SynergyOverviewSlot;
    planToDate: SynergyOverviewSlot;
    realized: SynergyOverviewSlot;
  }>;
  shortfalls: readonly SynergyOverviewShortfall[];
  rows: readonly SynergyOverviewRow[];
  sources: readonly SynergyOverviewSource[];
  summary?: SynergyOverviewSummary;
}>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string' && value !== '';

function readSlot(value: unknown): SynergyOverviewSlot | null {
  if (!isRecord(value)) return null;
  if (value.state === 'not_available')
    return isText(value.reason) ? { state: 'not_available', reason: value.reason } : null;
  if (value.state !== 'computed' || !isText(value.asOf)) return null;
  if (value.dueDate !== undefined && value.dueDate !== null && !isText(value.dueDate)) return null;
  const figure = readSynergyLoopFigure(value.figure);
  return figure === null ? null : { state: 'computed', figure, asOf: value.asOf,
    ...(value.dueDate === null || isText(value.dueDate) ? { dueDate: value.dueDate } : {}) };
}

function readRow(value: unknown): SynergyOverviewRow | null {
  if (!isRecord(value)) return null;
  const { commitmentRef, label, kind, status, statusText, owner } = value;
  if (!isText(commitmentRef) || !isText(label) || !isText(statusText) || !isText(owner)) return null;
  if (kind !== 'commitment' && kind !== 'overlay') return null;
  if (!SYNERGY_OVERVIEW_STATUSES.includes(status as SynergyOverviewStatus)) return null;
  // Promise authority decides an overlay; a row that says otherwise is off-contract.
  if ((kind === 'overlay') !== (status === 'overlay')) return null;
  const slots = ['target', 'planToDate', 'realized', 'behindPlan', 'forecastShortfall'] as const;
  const read = slots.map((slot) => readSlot(value[slot]));
  if (read.some((slot) => slot === null)) return null;
  const [target, planToDate, realized, behindPlan, forecastShortfall] =
    read as SynergyOverviewSlot[];
  const yearEndForecast = value.yearEndForecast === undefined ? undefined
    : readSlot(value.yearEndForecast);
  const yearEndShortfall = value.yearEndShortfall === undefined ? undefined
    : readSlot(value.yearEndShortfall);
  if (yearEndForecast === null || yearEndShortfall === null) return null;
  const reconciliations = readSynergyReconciliations(value.reconciliations);
  return { commitmentRef, label, kind, status: status as SynergyOverviewStatus, statusText, owner,
    target, planToDate, realized, behindPlan, forecastShortfall,
    ...(reconciliations.length ? { reconciliations } : {}),
    ...(yearEndForecast ? { yearEndForecast } : {}),
    ...(yearEndShortfall ? { yearEndShortfall } : {}) };
}

function readShortfall(value: unknown): SynergyOverviewShortfall | null {
  if (!isRecord(value)) return null;
  const shortfall = readSynergyLoopFigure(value.shortfall);
  if (!isText(value.commitmentRef) || !isText(value.label) || !isText(value.owner) ||
    !isText(value.asOf) || shortfall === null) return null;
  return { commitmentRef: value.commitmentRef, label: value.label, owner: value.owner, shortfall,
    asOf: value.asOf };
}

function readSummary(value: unknown): SynergyOverviewSummary | null {
  if (!isRecord(value) || !isText(value.source) || !Array.isArray(value.figures) ||
    !Array.isArray(value.sentences)) return null;
  const figures = value.figures.map(readSynergyLoopFigure);
  const sentences = value.sentences.map((sentence) =>
    Array.isArray(sentence) && sentence.length > 0 ? sentence.map(readSynergyLoopSegment) : [null]);
  if (figures.some((figure) => figure === null) ||
    sentences.some((sentence) => sentence.some((segment) => segment === null))) return null;
  return { sentences: sentences as SynergyLoopSegment[][],
    figures: figures as AskHelixNumericBindingDto[], source: value.source };
}

/** Strict reader: anything off-contract is refused whole (null), never partly guessed. */
export function readSynergyOverview(value: unknown): SynergyOverviewV1 | null {
  if (!isRecord(value) || value.schemaVersion !== SYNERGY_OVERVIEW_SCHEMA_VERSION) return null;
  const { dealRef, dealLabel, asOf, board } = value;
  if (!isText(dealRef) || !isText(dealLabel) || !isText(asOf) || !isRecord(board)) return null;
  const target = readSlot(board.target);
  const planToDate = readSlot(board.planToDate);
  const realized = readSlot(board.realized);
  if (!target || !planToDate || !realized) return null;
  if (!Array.isArray(value.rows) || !Array.isArray(value.shortfalls) || !Array.isArray(value.sources))
    return null;
  const rows = value.rows.map(readRow);
  const shortfalls = value.shortfalls.map(readShortfall);
  if (rows.some((row) => row === null) || shortfalls.some((entry) => entry === null)) return null;
  const summary = value.summary === undefined ? undefined : readSummary(value.summary);
  if (summary === null) return null;
  const sources = value.sources.flatMap((source) =>
    isRecord(source) && isText(source.datasetRef) && isText(source.revisionId) &&
    isText(source.digest)
      ? [{ datasetRef: source.datasetRef, revisionId: source.revisionId, digest: source.digest }]
      : []);
  return {
    schemaVersion: SYNERGY_OVERVIEW_SCHEMA_VERSION,
    dealRef, dealLabel, asOf,
    board: { target, planToDate, realized },
    shortfalls: shortfalls as SynergyOverviewShortfall[],
    rows: rows as SynergyOverviewRow[],
    sources,
    ...(summary ? { summary } : {}),
  };
}
